import React, { useRef, useState } from 'react';
import { JOURNEYS, createSandbox, deleteAllTestData, buildReport, notifyWorkspacesChanged } from '../services/testRunner';
import { LAB_SCRIPTS } from '../utils/labScripts';
import { confirmDialog } from '../utils/dialog';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const STORAGE_KEY = 'projectly_testapp_last';
function loadLast() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {}; }
  catch { return {}; }
}

/**
 * Admin-only "Test App" panel. Runs the in-app journeys against the real
 * backend inside a disposable Test Sandbox workspace, narrating each step.
 * Auto mode runs straight through; Manual mode pauses after each step until
 * you click Next (or "Auto-run the rest"). Ends with a copyable report and a
 * one-click "Delete all test data".
 *
 * NOTE: this is the logic/report layer. The visual cursor + on-canvas
 * highlighting ("watch it click") is the next increment on top of this.
 */
export default function TestAppPanel() {
  const saved = loadLast();
  const [mode, setMode] = useState('auto');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(saved.results || []); // { journey, label, status, detail }
  const [banner, setBanner] = useState(saved.banner || (saved.ranAt ? `Last run: ${new Date(saved.ranAt).toLocaleString('en-GB')}` : ''));
  const [report, setReport] = useState(saved.report || '');
  const [awaitingNext, setAwaitingNext] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState('');
  const [copyMsg, setCopyMsg] = useState('');

  const modeRef = useRef(mode);
  const nextResolver = useRef(null);

  const setModeBoth = (m) => { modeRef.current = m; setMode(m); };
  const gate = () => new Promise((res) => { nextResolver.current = res; });
  const advance = () => {
    if (nextResolver.current) { const r = nextResolver.current; nextResolver.current = null; setAwaitingNext(false); r(); }
  };

  const run = async () => {
    setRunning(true); setResults([]); setReport(''); setDeleteMsg('');
    const startedAt = new Date().toLocaleString('en-GB');
    const collected = [];
    try {
      setBanner('Creating a Test Sandbox workspace…');
      const workspaceId = await createSandbox();
      const ctx = { workspaceId };
      for (const j of JOURNEYS) {
        for (const step of j.steps) {
          setResults((p) => [...p, { journey: j.name, label: step.label, status: 'running', detail: '' }]);
          setBanner(`${j.name} → ${step.label}…`);
          let res;
          try { res = await step.run(ctx); } catch (e) { res = { ok: false, detail: e.message }; }
          const entry = { journey: j.name, label: step.label, status: res.ok ? 'pass' : 'fail', detail: res.detail };
          collected.push(entry);
          setResults((p) => { const c = [...p]; c[c.length - 1] = entry; return c; });
          setBanner(`${res.ok ? '✓' : '✗'} ${step.label}${res.detail ? ` — ${res.detail}` : ''}`);
          if (modeRef.current === 'manual') { setAwaitingNext(true); await gate(); }
          else { await sleep(700); }
        }
      }
      const finalReport = buildReport(collected, startedAt);
      const failed = collected.filter((r) => r.status === 'fail').length;
      const finalBanner = failed === 0 ? '✅ All journeys passed.' : `❌ ${failed} step(s) failed — see report.`;
      setReport(finalReport);
      setBanner(finalBanner);
      // Persist so returning to Settings shows the last run instead of a blank panel.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ results: collected, report: finalReport, banner: finalBanner, ranAt: Date.now() }));
      } catch { /* ignore quota */ }
    } catch (e) {
      setBanner(`Run error: ${e.message}`);
    } finally {
      setRunning(false); setAwaitingNext(false);
      // Refresh the workspace selector now the run is done, so the sandbox
      // shows up there to inspect. Done after the run to avoid disrupting it.
      notifyWorkspacesChanged();
    }
  };

  const onDelete = async () => {
    if (!(await confirmDialog({ title: 'Delete test data', message: 'Delete ALL Test Sandbox workspaces and their test notifications? This cannot be undone.', confirmText: 'Delete all', danger: true }))) return;
    setDeleting(true); setDeleteMsg('');
    try {
      const { workspacesRemoved, inboxRemoved } = await deleteAllTestData();
      setDeleteMsg(`Removed ${workspacesRemoved} sandbox workspace(s) and ${inboxRemoved} test notification(s).`);
      setResults([]); setReport(''); setBanner('');
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    } catch (e) {
      setDeleteMsg(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const copyReport = async () => {
    try { await navigator.clipboard.writeText(report); setCopyMsg('Copied!'); setTimeout(() => setCopyMsg(''), 2000); }
    catch { setCopyMsg('Press Ctrl/Cmd+C'); }
  };

  const icon = (s) => (s === 'pass' ? '✅' : s === 'fail' ? '❌' : '⏳');

  const startFollow = (labId, m) => {
    window.dispatchEvent(new CustomEvent('projectly_start_follow_test', { detail: { labId: Number(labId), mode: m } }));
  };

  return (
    <div className="settings-section">
      <h3>🧪 Test App <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' }}>(admin only)</span></h3>
      <p className="settings-desc">
        Runs through real user tasks against the live app inside a throwaway <strong>Test Sandbox</strong> workspace,
        so your real data is never touched. Watch each step, then copy the report.
      </p>

      {/* Follow test — drives the real UI so you watch the cursor click through it */}
      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'var(--bg-secondary, #0f172a)', border: '1px solid var(--border, #334155)' }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>👀 Follow test — watch it click through the app</div>
        {Object.entries(LAB_SCRIPTS).map(([id, lab]) => (
          <div key={id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ flex: '1 1 150px', fontSize: 13 }}>{lab.name}</span>
            <button data-testid={`follow-watch-${id}`} className="btn-primary" onClick={() => startFollow(id, 'auto')}>👀 Watch</button>
            <button data-testid={`follow-step-${id}`} onClick={() => startFollow(id, 'manual')} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border,#334155)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>Step</button>
          </div>
        ))}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
          Drives the real UI in a sandbox — an on-screen cursor clicks through it.
        </div>
      </div>

      {/* Quick check — fast, no-visuals backend run of all journeys */}
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>⚡ Quick check <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>— fast, no visuals</span></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="btn-primary" onClick={run} disabled={running}>
          {running ? 'Running…' : '⚡ Run quick check'}
        </button>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border, #334155)', borderRadius: 8, overflow: 'hidden' }}>
          {['auto', 'manual'].map((m) => (
            <button
              key={m}
              onClick={() => setModeBoth(m)}
              style={{
                padding: '6px 12px', border: 'none', cursor: 'pointer', fontSize: 13,
                background: mode === m ? 'var(--accent-primary, #6366f1)' : 'transparent',
                color: mode === m ? '#fff' : 'var(--text-secondary)',
              }}
            >{m === 'auto' ? 'Auto-run' : 'Manual (step)'}</button>
          ))}
        </div>
        {awaitingNext && (
          <>
            <button className="btn-primary" onClick={advance}>Next ▶</button>
            <button onClick={() => { setModeBoth('auto'); advance(); }} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border,#334155)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              Auto-run the rest
            </button>
          </>
        )}
      </div>

      {/* Live banner */}
      {banner && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 14, fontWeight: 600,
          background: 'var(--bg-secondary, #1e293b)', borderLeft: '3px solid var(--accent-primary, #6366f1)',
        }}>{banner}</div>
      )}

      {/* Step list */}
      {results.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          {results.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 13, borderBottom: '1px solid var(--border-light, #1e293b)' }}>
              <span>{icon(r.status)}</span>
              <span style={{ flex: 1 }}>
                <span style={{ color: 'var(--text-secondary)' }}>{r.journey}:</span> {r.label}
                {r.detail && <span style={{ color: 'var(--text-secondary)' }}> — {r.detail}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Report */}
      {report && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Report</strong>
            <button onClick={copyReport} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border,#334155)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12 }}>
              Copy report
            </button>
            {copyMsg && <span style={{ fontSize: 12, color: 'var(--success-text, #34d399)' }}>{copyMsg}</span>}
          </div>
          <textarea readOnly value={report} style={{
            width: '100%', minHeight: 180, fontFamily: 'monospace', fontSize: 12,
            padding: 10, borderRadius: 8, border: '1px solid var(--border, #334155)',
            background: 'var(--bg-secondary, #0f172a)', color: 'var(--text-primary, #e2e8f0)', resize: 'vertical',
          }} />
        </div>
      )}

      {/* Cleanup */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border, #334155)' }}>
        <button onClick={onDelete} disabled={deleting} style={{
          padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444', background: 'transparent',
          color: '#ef4444', cursor: 'pointer', fontWeight: 600,
        }}>{deleting ? 'Deleting…' : '🗑 Delete all test data'}</button>
        {deleteMsg && <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{deleteMsg}</span>}
      </div>
    </div>
  );
}
